import { useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faLock } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import ActionButton from "../ui/ActionButton";
import BaseDialog from "../ui/BaseDialog";
import "../../style/ReceiverInventory.css";

const EDIT_WINDOW_DAYS = 30;

const REJECTION_REASONS = ["wrong_material", "contaminated", "damaged", "expired", "other"];

const reasonLabelKey = (r) =>
  ({
    wrong_material: "Wrong material",
    contaminated: "Contaminated",
    damaged: "Damaged",
    expired: "Expired",
    other: "Other",
  }[r]);

const round = (v, decimals) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return parseFloat(n.toFixed(decimals)).toString();
};

const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(d);
  }
};

const daysSince = (d) => {
  if (!d) return null;
  const then = new Date(d).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
};

const ReceiverRejectionInput = ({
  open,
  onClose,
  onSubmit,
  onSaved,
  darkMode = false,
  deliveredQty,
  donor,
  material,
  initialAccepted = null,
  initialReason = null,
  initialNotes = null,
  firstLoggedAt = null,
  dateModified = null,
}) => {
  const { t } = useTranslation();

  const effectiveInitial = initialAccepted != null ? initialAccepted : deliveredQty;
  const daysSinceFirstLog = daysSince(firstLoggedAt);
  const isLocked = daysSinceFirstLog != null && daysSinceFirstLog >= EDIT_WINDOW_DAYS;
  const isEdit = firstLoggedAt != null;

  const [phase, setPhase] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [accepted, setAccepted] = useState(() => effectiveInitial);
  const [pctInput, setPctInput] = useState(() =>
    deliveredQty > 0 ? round((effectiveInitial / deliveredQty) * 100, 2) : "100"
  );
  const [inputMode, setInputMode] = useState("lbs");
  const [reason, setReason] = useState(initialReason || "");
  const [notes, setNotes] = useState(initialNotes || "");
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const handleLbsChange = (v) => {
    setAccepted(v);
    const n = Number(v);
    if (Number.isFinite(n) && deliveredQty > 0) {
      setPctInput(round((n / deliveredQty) * 100, 2));
    }
  };

  const handlePctChange = (v) => {
    setPctInput(v);
    const n = parseFloat(v);
    if (Number.isFinite(n)) {
      setAccepted((n / 100) * deliveredQty);
    } else if (v === "") {
      setAccepted(0);
    }
  };

  const rejected = Math.max(0, deliveredQty - Number(accepted || 0));
  const needsReason = rejected > 0;
  const isOther = reason === "other";

  const qtyChanged = Math.abs(Number(accepted) - Number(effectiveInitial)) > 0.001;
  const reasonChanged = (reason || "") !== (initialReason || "");
  const notesChanged = (notes || "").trim() !== (initialNotes || "").trim();
  const hasChanges = qtyChanged || reasonChanged || notesChanged;

  const handleSubmit = async () => {
    const acceptedNum = Number(accepted);
    if (Number.isNaN(acceptedNum) || acceptedNum < 0 || acceptedNum > deliveredQty) {
      setErrorMsg(t("Accepted quantity must be between 0 and {{max}}", { max: deliveredQty }));
      return;
    }
    if (needsReason && !reason) {
      setErrorMsg(t("Please select a reason for the rejected portion"));
      return;
    }
    if (needsReason && isOther && !notes.trim()) {
      setErrorMsg(t("Please describe the reason"));
      return;
    }

    setErrorMsg("");
    setPhase("submitting");

    const payload = {
      quantity_accepted: acceptedNum,
      rejection_reason: needsReason ? reason : null,
      rejection_disposition: needsReason ? "disposed_by_receiver" : null,
      notes: needsReason && notes.trim() ? notes.trim() : null,
    };

    try {
      await onSubmit(payload);
      setSavedSnapshot({
        accepted: acceptedNum,
        rejected,
        reason: needsReason ? reason : null,
        notes: payload.notes,
      });
      setPhase("saved");
      if (onSaved) onSaved();
    } catch (err) {
      setErrorMsg(err?.message || t("Failed to log acceptance"));
      setPhase("idle");
    }
  };

  const submitting = phase === "submitting";
  const isSaved = phase === "saved";
  const fmt = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

  // locked view — past the edit window, read-only summary only
  if (isLocked && !isSaved) {
    return (
      <BaseDialog
        open={open}
        onClose={onClose}
        darkMode={darkMode}
        title={t("Record finalized")}
        size="sm"
        actions={
          <ActionButton onClick={onClose} variant="primary" modal>
            {t("Close")}
          </ActionButton>
        }
      >
        <div className="rri-locked">
          <FontAwesomeIcon icon={faLock} className="rri-locked-icon" />
          <p className="rri-locked-headline">
            {t("This record was finalized on {{date}}.", { date: formatDate(firstLoggedAt) })}
          </p>
          <div className="rri-success-summary">
            <div>
              <span className="rri-success-label">{t("Delivered")}:</span>{" "}
              <strong>{fmt(deliveredQty)} {t("lbs")}</strong>
            </div>
            <div>
              <span className="rri-success-label">{t("Accepted")}:</span>{" "}
              <strong>{fmt(effectiveInitial)} {t("lbs")}</strong>
            </div>
            {initialReason && (
              <div>
                <span className="rri-success-label">{t("Reason")}:</span>{" "}
                <strong>{t(reasonLabelKey(initialReason))}</strong>
              </div>
            )}
            {initialNotes && (
              <div className="rri-success-notes">
                <span className="rri-success-label">{t("Notes")}:</span>{" "}
                <em>{initialNotes}</em>
              </div>
            )}
          </div>
          <p className="rri-success-hint">
            {t("Contact support to request a correction.")}
          </p>
        </div>
      </BaseDialog>
    );
  }

  return (
    <BaseDialog
      open={open}
      onClose={submitting ? () => {} : onClose}
      darkMode={darkMode}
      title={isSaved ? t("Acceptance logged") : t("Post-receival review")}
      size="sm"
      ariaLabelledBy="rejection-input-title"
      ariaDescribedBy="rejection-input-desc"
      actions={
        isSaved ? (
          <ActionButton onClick={onClose} variant="primary" modal>
            {t("Done")}
          </ActionButton>
        ) : (
          <>
            <ActionButton onClick={onClose} variant="default" modal disabled={submitting}>
              {t("Cancel")}
            </ActionButton>
            <ActionButton
              onClick={handleSubmit}
              variant="primary"
              modal
              loading={submitting}
              disabled={!hasChanges}
            >
              {t("Save")}
            </ActionButton>
          </>
        )
      }
    >
      {isSaved && savedSnapshot ? (
        <div className="rri-success">
          <FontAwesomeIcon icon={faCircleCheck} className="rri-success-icon" />
          <p className="rri-success-headline">{t("Saved for this pickup")}</p>
          <div className="rri-success-summary">
            <div>
              <span className="rri-success-label">{t("Accepted")}:</span>{" "}
              <strong>{fmt(savedSnapshot.accepted)} {t("lbs")}</strong>
            </div>
            {savedSnapshot.rejected > 0 && (
              <>
                <div>
                  <span className="rri-success-label">{t("Rejected")}:</span>{" "}
                  <strong>{fmt(savedSnapshot.rejected)} {t("lbs")}</strong>
                </div>
                {savedSnapshot.reason && (
                  <div>
                    <span className="rri-success-label">{t("Reason")}:</span>{" "}
                    <strong>{t(reasonLabelKey(savedSnapshot.reason))}</strong>
                  </div>
                )}
                {savedSnapshot.notes && (
                  <div className="rri-success-notes">
                    <span className="rri-success-label">{t("Notes")}:</span>{" "}
                    <em>{savedSnapshot.notes}</em>
                  </div>
                )}
              </>
            )}
          </div>
          <p className="rri-success-hint">
            {t("The donor will see this in their matching insights.")}
          </p>
        </div>
      ) : (
        <>
          <div className="rri-context">
            <span className="rri-context-label">{t("Donor")}:</span>
            <span>{donor || "—"}</span>
            <span className="rri-context-label">{t("Material")}:</span>
            <span>{material || "—"}</span>
            <span className="rri-context-label">{t("Delivered")}:</span>
            <span>{fmt(deliveredQty)} {t("lbs")}</span>
          </div>

          {isEdit && dateModified && (
            <p className="rri-edit-meta">
              {t("Last updated")}: {formatDate(dateModified)}
              {" · "}
              {t("Locks in {{days}} days", { days: Math.max(0, EDIT_WINDOW_DAYS - (daysSinceFirstLog || 0)) })}
            </p>
          )}

          <div className="rri-field">
            <label className="rri-label">{t("How much was usable?")}</label>
            <div className="rri-input-dual">
              <label className={`rri-input-tile ${inputMode === "lbs" ? "rri-input-tile--active" : ""}`}>
                <input
                  type="number"
                  min="0"
                  max={deliveredQty}
                  step="0.01"
                  value={inputMode === "lbs" ? accepted : round(accepted, 2)}
                  onChange={(e) => handleLbsChange(e.target.value)}
                  onFocus={() => !submitting && setInputMode("lbs")}
                  readOnly={inputMode !== "lbs"}
                  disabled={submitting}
                  className="rri-input rri-input--number"
                  aria-label={t("Accepted quantity in lbs")}
                />
                <span className="rri-input-unit">{t("lbs")}</span>
              </label>
              <label className={`rri-input-tile ${inputMode === "percent" ? "rri-input-tile--active" : ""}`}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={pctInput}
                  onChange={(e) => handlePctChange(e.target.value)}
                  onFocus={() => !submitting && setInputMode("percent")}
                  readOnly={inputMode !== "percent"}
                  disabled={submitting}
                  className="rri-input rri-input--number"
                  aria-label={t("Accepted quantity in percent")}
                />
                <span className="rri-input-unit">%</span>
              </label>
            </div>
            {rejected > 0 && (
              <div className="rri-rejected-hint">
                {t("Rejected")}: {fmt(rejected)} {t("lbs")}
              </div>
            )}
          </div>

          {needsReason && (
            <div className="rri-field">
              <label htmlFor="rri-reason" className="rri-label">{t("Reason")}</label>
              <select
                id="rri-reason"
                className="rri-select"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submitting}
              >
                <option value="" disabled>{t("Select a reason")}</option>
                {REJECTION_REASONS.map((r) => (
                  <option key={r} value={r}>{t(reasonLabelKey(r))}</option>
                ))}
              </select>
            </div>
          )}

          {needsReason && (isOther || notes) && (
            <div className="rri-field">
              <label htmlFor="rri-notes" className="rri-label">
                {isOther ? t("Please describe") : t("Notes (optional)")}
                {isOther && <span className="rri-required"> *</span>}
              </label>
              <textarea
                id="rri-notes"
                rows={3}
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="rri-textarea"
                placeholder={isOther ? t("Describe the issue…") : t("Optional details for the donor")}
                disabled={submitting}
              />
            </div>
          )}

          {needsReason && !isOther && !notes && (
            <button
              type="button"
              className="rri-notes-toggle"
              onClick={() => setNotes(" ")}
              disabled={submitting}
            >
              + {t("Add optional notes for donor")}
            </button>
          )}

          {needsReason && (
            <p className="rri-disposition-note">
              {t("Unusable portion will be marked as disposed locally.")}
            </p>
          )}

          {errorMsg && <div className="rri-error">{errorMsg}</div>}
        </>
      )}
    </BaseDialog>
  );
};

ReceiverRejectionInput.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  darkMode: PropTypes.bool,
  deliveredQty: PropTypes.number.isRequired,
  donor: PropTypes.string,
  material: PropTypes.string,
  initialAccepted: PropTypes.number,
  initialReason: PropTypes.string,
  initialNotes: PropTypes.string,
  firstLoggedAt: PropTypes.string,
  dateModified: PropTypes.string,
};

export default ReceiverRejectionInput;
